import type { AssetId } from '@shapeshiftoss/caip'
import type { HistoryData, HistoryTimeframe, PartialRecord } from '@shapeshiftoss/types'
import { getHistoryTimeframeBounds } from '@shapeshiftoss/utils'
import dayjs from 'dayjs'
import { merge, orderBy } from 'lodash'

import type { PriceHistoryByTimeframe } from './types'

import type { SupportedFiatCurrencies } from '@/lib/market-service'

export const getTrimmedOutOfBoundsMarketData = <T extends SupportedFiatCurrencies | AssetId>(
  priceHistory: PriceHistoryByTimeframe<T>,
  timeframe: HistoryTimeframe,
  ids: T[],
) => {
  const timeFrameData = priceHistory[timeframe]
  if (!timeFrameData) return

  const { start, end } = getHistoryTimeframeBounds(timeframe, dayjs())

  const startTimeStampMillis = start.valueOf()
  const endTimeStampMillis = end.valueOf()

  const results: PartialRecord<T, HistoryData[]> = {}

  for (const id of ids) {
    const idHistory = timeFrameData[id]
    if (!idHistory) continue

    const filteredResults = idHistory.filter(
      ({ date }) => date >= startTimeStampMillis && date <= endTimeStampMillis,
    )

    // Sort the historical entries.
    results[id] = orderBy(filteredResults, 'date', 'asc')
  }

  return results
}

export const trimOutOfBoundsMarketData = <T extends SupportedFiatCurrencies | AssetId>(
  priceHistory: PriceHistoryByTimeframe<T>,
  timeframe: HistoryTimeframe,
  ids: T[],
) => {
  const trimmedTimeframeMarketData = getTrimmedOutOfBoundsMarketData(priceHistory, timeframe, ids)

  if (!trimmedTimeframeMarketData) return priceHistory

  return merge(priceHistory[timeframe], trimmedTimeframeMarketData)
}
